import { GOAL_METRICS, type GoalMetric } from './stats';

export const OVERVIEW_VITALS = [
  'open_tasks', 'active_projects', 'outstanding', 'overdue', ...GOAL_METRICS,
] as const;
export type OverviewVital = (typeof OVERVIEW_VITALS)[number];
export type TargetPeriod = 'month' | 'quarter';

export interface OverviewTargetsSettings {
  targets: GoalMetric[];
  targets_order: 'pace' | 'listed';
  targets_max: number;
  vitals_max: number;
  vitals: OverviewVital[];
  target_periods?: Partial<Record<GoalMetric, TargetPeriod[]>>;
}

export type EffectiveOverviewTargetsSettings = OverviewTargetsSettings & {
  target_periods: Record<GoalMetric, TargetPeriod[]>;
};

/** Legacy rows and omitted metrics continue to track both periods. */
export function effectiveOverviewTargetsSettings(settings: OverviewTargetsSettings): EffectiveOverviewTargetsSettings {
  const target_periods = {} as Record<GoalMetric, TargetPeriod[]>;
  for (const metric of GOAL_METRICS) {
    target_periods[metric] = [...(settings.target_periods?.[metric] ?? ['month', 'quarter'])];
  }
  return { ...settings, target_periods };
}

export const DEFAULT_OVERVIEW_TARGETS_SETTINGS: OverviewTargetsSettings = {
  targets: [...GOAL_METRICS],
  targets_order: 'pace',
  targets_max: 3,
  vitals_max: 4,
  vitals: ['open_tasks', 'active_projects', 'outstanding', 'overdue'],
};

export interface OverviewTarget {
  metric: GoalMetric;
  period: TargetPeriod;
  period_key: string;
  target: number;
  actual: number;
  carried_forward: boolean;
  source_period_key?: string;
}

/** A locked-in lead whose start date (`resurface_date`) has arrived —
    Overview's "Convert to project" nudge (`GET /api/admin/overview`
    `locked_in_due`, ordered by resurface_date). */
export interface OverviewLockedInDue {
  id: string;
  name: string;
  company: string | null;
  resurface_date: string;
  value_cents: number | null;
}

/** Full replacement only: reject unknown keys, duplicates and coercions. */
export function isOverviewTargetsSettings(value: unknown): value is OverviewTargetsSettings {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const settings = value as Record<string, unknown>;
  const subset = (items: unknown, allowed: readonly string[]) =>
    Array.isArray(items) && items.every(item => typeof item === 'string' && allowed.includes(item))
    && new Set(items).size === items.length;
  const allowedKeys = ['targets', 'targets_order', 'targets_max', 'vitals_max', 'vitals', 'target_periods'];
  const periods = settings.target_periods;
  if (Object.prototype.hasOwnProperty.call(settings, 'target_periods')) {
    if (!periods || typeof periods !== 'object' || Array.isArray(periods)) return false;
    if (!Object.entries(periods).every(([metric, values]) =>
      GOAL_METRICS.includes(metric as GoalMetric) && Array.isArray(values)
      && values.length > 0 && subset(values, ['month', 'quarter']))) return false;
  }
  return Object.keys(settings).every(key => allowedKeys.includes(key))
    && allowedKeys.slice(0, 5).every(key => Object.prototype.hasOwnProperty.call(settings, key))
    && subset(settings.targets, GOAL_METRICS)
    && typeof settings.targets_max === 'number' && Number.isInteger(settings.targets_max)
    && settings.targets_max >= 1 && settings.targets_max <= 8
    && subset(settings.vitals, OVERVIEW_VITALS)
    && typeof settings.vitals_max === 'number' && Number.isInteger(settings.vitals_max)
    && settings.vitals_max >= 1 && settings.vitals_max <= 6
    && (settings.targets_order === 'pace' || settings.targets_order === 'listed');
}

export interface PreviousOverviewGoal {
  /** Absent in old cached responses; unknown availability is not actionable. */
  copyable?: boolean;
  metric: GoalMetric;
  period: TargetPeriod;
  period_key: string;
  target: number;
}
